import {
  AssignmentQueryParams,
  AssignmentSearchParams,
  assignmentSearchParams,
} from '../../lib/assignment-context';
import {
  ActionResult,
  AssignLaboratoryDto,
  AssignSamplerDto,
  ConfirmLaboratoryDto,
  ForceOrganicOkResult,
  GpAssignmentMapResponse,
  LaboratoryMini,
  NgpRecapResponse,
  NgpSamplerOption,
  NgpSamplerOrganizationOption,
  PreviewPolygonDto,
  PreviewPolygonResponse,
  ReassignSamplerDto,
  SmsBatchProgress,
} from '../types/gp-sampling-assignment';

import { handleRequest, privateClient } from '@data-access';

function withContext<T>(dto: T, context: AssignmentQueryParams): T & AssignmentSearchParams {
  return {
    ...dto,
    ...assignmentSearchParams(context),
  };
}

export function createSamplingAssignmentApi(context: AssignmentQueryParams) {
  const searchParams = assignmentSearchParams(context);

  return {
    map: {
      get: async (referenceDate?: string) => {
        return privateClient
          .get('gp-sampling-assignment-map/', {
            searchParams: {
              ...searchParams,
              ...(referenceDate ? { reference_date: referenceDate } : {}),
            },
          })
          .json<GpAssignmentMapResponse>();
      },
      preview: async (dto: PreviewPolygonDto) => {
        return privateClient
          .post('gp-sampling-assignment-map/preview/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<PreviewPolygonResponse>();
      },
    },
    actions: {
      assignSampler: async (dto: AssignSamplerDto) => {
        return privateClient
          .post('gp-sampling-assignment-map/actions/assign-sampler/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<ActionResult>();
      },
      assignLaboratory: async (dto: AssignLaboratoryDto) => {
        return privateClient
          .post('gp-sampling-assignment-map/actions/assign-laboratory/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<ActionResult>();
      },
      sendSms: async (dto: Omit<AssignSamplerDto, 'sampler' | 'visible'> & { period: string }) => {
        return handleRequest(
          privateClient
            .post('gp-sampling-assignment-map/actions/send-sms/', {
              json: withContext(dto, context),
              searchParams,
            })
            .json<ActionResult>(),
        );
      },
      makeVisible: async (dto: Record<string, unknown>) => {
        return privateClient
          .post('gp-sampling-assignment-map/actions/make-visible/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<ActionResult>();
      },
      reassignSampler: async (dto: ReassignSamplerDto) => {
        return privateClient
          .post('gp-sampling-assignment-map/actions/reassign-sampler/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<ActionResult>();
      },
      confirmLaboratory: async (dto: ConfirmLaboratoryDto) => {
        return privateClient
          .post('gp-sampling-assignment-map/actions/confirm-laboratory/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<ActionResult>();
      },
      forceOrganicOk: async (dto: { point_id: number; forced: boolean }) => {
        return privateClient
          .post('gp-sampling-assignment-map/actions/force-organic-ok/', {
            json: withContext(dto, context),
            searchParams,
          })
          .json<ForceOrganicOkResult>();
      },
    },
    laboratories: {
      list: async () => {
        return privateClient
          .get('gp-sampling-assignment-map/laboratories/', { searchParams })
          .json<LaboratoryMini[]>();
      },
    },
    samplers: {
      list: async (organizationId?: number) => {
        return privateClient
          .get('gp-sampling-assignment-map/samplers/', {
            searchParams: {
              ...searchParams,
              ...(organizationId ? { organization_id: organizationId } : {}),
            },
          })
          .json<NgpSamplerOption[]>();
      },
    },
    samplerOrganizations: {
      list: async () => {
        return privateClient
          .get('gp-sampling-assignment-map/sampler-organizations/', { searchParams })
          .json<NgpSamplerOrganizationOption[]>();
      },
    },
    recap: {
      list: async (params?: {
        samplerIds?: number[];
        visible?: boolean;
        period?: string;
        view?: string;
      }) => {
        const recapParams = new URLSearchParams();
        for (const [key, value] of Object.entries(searchParams)) {
          recapParams.set(key, String(value));
        }
        if (params?.samplerIds?.length) {
          for (const id of params.samplerIds) {
            recapParams.append('sampler_ids', String(id));
          }
        }
        if (params?.visible !== undefined) {
          recapParams.set('visible', params.visible ? 'true' : 'false');
        }
        if (params?.period) recapParams.set('period', params.period);
        if (params?.view) recapParams.set('view', params.view);
        return privateClient
          .get('gp-sampling-assignment-map/recap/', { searchParams: recapParams })
          .json<NgpRecapResponse>();
      },
    },
    smsProgress: async (dispatchId: number) => {
      return privateClient
        .get(`gp-sampling-assignment-map/sms-progress/${dispatchId}/`, { searchParams })
        .json<SmsBatchProgress>();
    },
  };
}
